/**
 * Sides inside one bet card. Since Oct 1, 2026 a card holds one team/player +
 * market for a game with BOTH sides (client: "Spurs team total" showed two
 * Spurs cards — Over and Under — and "Hurricanes 1st period puck line" split
 * into −/+ cards). The card shows a switch and lists one side's lines at a time.
 *
 *   sideOfLine(line)      'over' | 'under' | 'minus' | 'plus' | null
 *   cardSides(lines)      sides present, in display order ([over, under] / [minus, plus])
 *   defaultCardSide(...)  the side the user named, else the first line's side
 *                         (the renderer puts the card's best-value line first)
 */
const SIDE_ORDER = ['over', 'under', 'minus', 'plus'];

export const SIDE_LABELS = { over: 'Over', under: 'Under', minus: '−', plus: '+' };

function selectionOf(line) {
    return String(line?.raw?.selection || line?.selection || '');
}

function signedNumber(line) {
    const text = selectionOf(line);
    const m = text.match(/(?:^|\s)([+-]\d+(?:\.\d+)?)\s*$/);
    if (m) return Number(m[1]);
    for (const c of [line?.lineValue, line?.raw?.handicap, line?.raw?.selection_line, line?.raw?.line, line?.raw?.points]) {
        const n = typeof c === 'number' ? c : Number(String(c ?? '').trim() || NaN);
        if (Number.isFinite(n)) return n;
    }
    return null;
}

export function sideOfLine(line) {
    const sel = selectionOf(line);
    if (/\bover\b/i.test(sel)) return 'over';
    if (/\bunder\b/i.test(sel)) return 'under';
    const n = signedNumber(line);
    if (n === null) return null;
    return n < 0 ? 'minus' : 'plus';
}

export function cardSides(lines) {
    const present = new Set();
    for (const l of lines || []) {
        const s = sideOfLine(l);
        if (s) present.add(s);
    }
    return SIDE_ORDER.filter((s) => present.has(s));
}

/**
 * requestedDirection is the query's 'over' / 'under'; for spreads the parser
 * reads "+N" as over and "-N" as under, so those map to plus / minus.
 */
export function defaultCardSide(lines, requestedDirection = null) {
    const sides = cardSides(lines);
    if (sides.length === 0) return null;
    const wanted = requestedDirection === 'over' ? ['over', 'plus']
        : requestedDirection === 'under' ? ['under', 'minus'] : [];
    const named = wanted.find((s) => sides.includes(s));
    if (named) return named;
    const first = sideOfLine((lines || [])[0]);
    return first && sides.includes(first) ? first : sides[0];
}
